import { memo } from 'react'

import {
    Box,
    Button,
    Dialog,
    Divider,
    IconButton,
    makeStyles,
    Typography,
    Checkbox,
    List,
    TextField,
    ListItemSecondaryAction,
    ListItem,
    ListItemIcon,
    ListItemText,
    LinearProgress,
    BoxProps
} from '@material-ui/core'

import CloseIcon from '@material-ui/icons/Close'
import AddIcon from '@material-ui/icons/Add'
import CheckIcon from '@material-ui/icons/Check'
import LockOutlinedIcon from '@material-ui/icons/LockOutlined'
import PublicIcon from '@material-ui/icons/PublicOutlined'

import { ReactComponent as TeamIcon } from 'assets/icons/team-icon.svg'
import { ReactComponent as ConnectedIcon } from 'assets/icons/connected-icon.svg'

import { PlaybookVisibilityType } from 'app/types'

import { TextOverflow } from '@guidde-co/shared.text-overflow.text-overflow'

const maxLength = 150

type StyleProps = {
    maxWidth: BoxProps['maxWidth']
}

const useStyles = makeStyles({
    paper: ({ maxWidth }: StyleProps) => ({
        maxWidth
    }),
    itemIcon: {
        minWidth: '30px'
    },
    list: {
        maxHeight: 300,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: 0
    }
})

const visibilityIcons = {
    Private: <LockOutlinedIcon style={{ height: 20 }} />,
    Team: <TeamIcon style={{ height: 20 }} />,
    Connected: <ConnectedIcon style={{ height: 20 }} />,
    Public: <PublicIcon style={{ height: 20 }} />
} as const

const buttonStyles = {
    boxShadow: 'none',
    textTransform: 'uppercase',
    letterSpacing: '1.25 px'
} as const

type Props = {
    isOpen: boolean
    isLoading?: boolean
    label?: string
    createButtonLabel?: string
    header: string
    setTitle: (title: string) => void
    onClose: () => void
    onAddClick?: () => void
    onItemClick?: (id: string, isSelected: boolean) => void
    onCreateClick: () => void
    showCreateButton: boolean
    options: Array<{
        id: string
        name: string
        isSelected: boolean
        isDisabled?: boolean
        visibility?: PlaybookVisibilityType
    }>
    title: string
    disableCreate?: boolean
    buttonLabel: string
    maxWidth?: BoxProps['maxWidth']
}

export const ShareCommonDialog = memo(
    ({
        maxWidth = '600px',
        isOpen,
        isLoading,
        title,
        buttonLabel,
        disableCreate,
        showCreateButton,
        createButtonLabel = 'Create',
        options,
        label,
        header,
        onCreateClick,
        setTitle,
        onAddClick = () => {},
        onItemClick = () => {},
        onClose
    }: Props) => {
        const classes = useStyles({ maxWidth })

        return (
            <Dialog
                open={isOpen}
                onClose={onClose}
                fullWidth
                maxWidth="xs"
                classes={{
                    paper: classes.paper
                }}
            >
                <Box minHeight={5}>{isLoading && <LinearProgress />}</Box>
                <Box px="8px" py="18px">
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mt="-15px"
                        mr="-8px"
                    >
                        <Box px="8px">
                            <Typography style={{ fontWeight: 500, fontSize: 20 }}>
                                {header}
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose}>
                            <CloseIcon style={{ color: '#000' }} />
                        </IconButton>
                    </Box>
                    <Divider />
                    <List className={classes.list}>
                        {options.map(item => (
                            <ListItem
                                key={item.id}
                                button
                                divider
                                style={{
                                    padding: '4px 12px',
                                    minHeight: 50
                                }}
                                disableGutters
                                onClick={() => onItemClick(item.id, item.isSelected)}
                                disabled={item.isDisabled || isLoading}
                                selected={item.isSelected}
                            >
                                <ListItemIcon classes={{ root: classes.itemIcon }}>
                                    <Checkbox
                                        edge="start"
                                        color="primary"
                                        checked={item.isSelected}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <TextOverflow lineClamp={1}>
                                            <Typography
                                                style={{
                                                    fontWeight: 500,
                                                    fontSize: 14,
                                                    width: item.visibility
                                                        ? 'calc(100% - 20px)'
                                                        : '100%'
                                                }}
                                            >
                                                {item.name}
                                            </Typography>
                                        </TextOverflow>
                                    }
                                />
                                {item.visibility && (
                                    <ListItemSecondaryAction color="#1C1B1F">
                                        {visibilityIcons[item.visibility]}
                                    </ListItemSecondaryAction>
                                )}
                            </ListItem>
                        ))}
                    </List>

                    {showCreateButton && (
                        <Box mt={1} mb={4}>
                            <Typography style={{ fontWeight: 500, fontSize: 14 }}>
                                {label}
                            </Typography>
                            <Box mt={2} />
                            <TextField
                                variant="filled"
                                label="Title"
                                value={title}
                                multiline
                                maxRows={3}
                                inputProps={{
                                    maxLength
                                }}
                                onChange={e => setTitle(e.target.value)}
                                fullWidth
                            />
                            <Box display="flex" justifyContent="flex-end">
                                <Typography color="textSecondary" variant="caption">
                                    {title.length}/{maxLength}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    <Box
                        display="flex"
                        justifyContent={showCreateButton ? 'flex-end' : 'flex-start'}
                        mt={1}
                    >
                        {showCreateButton ? (
                            <Button
                                style={buttonStyles}
                                onClick={onCreateClick}
                                variant="contained"
                                disabled={disableCreate || isLoading}
                                color="primary"
                                startIcon={<CheckIcon color="inherit" />}
                            >
                                {createButtonLabel}
                            </Button>
                        ) : (
                            <Button
                                style={{
                                    ...buttonStyles,
                                    color: 'rgba(9, 12, 16, 0.6)',
                                    background: '#F5F5F5'
                                }}
                                onClick={onAddClick}
                                variant="contained"
                                color="default"
                                startIcon={<AddIcon color="inherit" />}
                            >
                                {buttonLabel}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Dialog>
        )
    }
)
